import { useRef, useEffect, useState } from 'react';
import './Experience.css';

const EXPERIENCE = [
  {
    id: 'bereal',
    role: 'Product Management Externship',
    company: 'BeReal',
    type: 'Externship Program',
    period: 'Apr 2026 – Present',
    current: true,
    description: 'Conduct qualitative user research and analyze employee feedback to identify product improvements and UX gaps. Translate insights into product recommendations, wireframes, and UI/UX design ideas, while using AI-powered tools to support data synthesis and feature ideation.',
    tags: ['User Research', 'UI/UX', 'Product Strategy', 'AI Tools'],
  },
  {
    id: 'fetch',
    role: 'Pitch Competition Finalist',
    company: 'Fetch Student Sprint',
    type: 'Competition',
    period: 'Sep 2025 – Oct 2025',
    description: "Designed and pitched REVU, an AI-powered receipt analysis concept providing spending insights. Created an interactive prototype and presented the concept to judges, faculty, and industry professionals.",
    tags: ['Product Design', 'Prototyping', 'AI', 'Pitching'],
  },
  {
    id: 'research',
    role: 'Undergraduate Research Assistant',
    company: 'Suffolk University',
    type: 'Research',
    period: 'Oct 2025 – Present',
    current: true,
    description: 'Assist in developing an AI-driven academic planning system for student course guidance. Build models that generate personalized four-year pathways and natural-language prerequisite explanations.',
    tags: ['Python', 'ML', 'AI', 'Data'],
  },
];

const EXTRAS = [
  {
    role: 'Founder & Treasurer',
    org: 'Viva Veggies (Vegan Club) · Suffolk University',
    desc: 'Established a student-run sustainability club from scratch, managing finances and organizing campus events.',
  },
  {
    role: 'Member',
    org: 'Computer Science Club · Suffolk University',
    desc: 'Participating in programming events, collaborative projects, and technical workshops.',
  },
];

function useInView(threshold = 0.1) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setInView(true); obs.disconnect(); }
    }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

export default function Experience() {
  const [ref, inView] = useInView();

  return (
    <section className="section" id="experience">
      <div className="container">
        <div className="section-label">background</div>
        <h2 className="section-title">Experience &amp; Education</h2>

        <div className="exp-grid" ref={ref}>
          <div className={`exp-left ${inView ? 'animate-up' : ''}`}>
            <div className="exp-list">
              {EXPERIENCE.map((e, i) => (
                <div
                  key={e.id}
                  className="exp-item terminal-card"
                  style={{ animationDelay: `${i * 0.1}s` }}
                >
                  <div className="terminal-header">
                    <span className="dot" style={{ background: '#FF5F57' }} />
                    <span className="dot" style={{ background: '#FEBC2E' }} />
                    <span className="dot" style={{ background: '#28C840' }} />
                    <span className="term-label">{e.company.toLowerCase().replace(/\s/g, '-')}.log</span>
                    {e.current && <span className="current-badge">current</span>}
                  </div>
                  <div className="exp-body">
                    <div className="exp-header-row">
                      <div>
                        <div className="exp-role">{e.role}</div>
                        <div className="exp-company">{e.company} · <span className="exp-type">{e.type}</span></div>
                      </div>
                      <div className="exp-period">{e.period}</div>
                    </div>
                    <p className="exp-desc">{e.description}</p>
                    <div className="exp-tags">
                      {e.tags.map(t => <span key={t} className="exp-tag">{t}</span>)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className={`exp-right ${inView ? 'animate-up' : ''}`} style={{ animationDelay: '0.2s' }}>
            {/* Education */}
            <div className="terminal-card exp-edu">
              <div className="terminal-header">
                <span className="dot" style={{ background: '#FF5F57' }} />
                <span className="dot" style={{ background: '#FEBC2E' }} />
                <span className="dot" style={{ background: '#28C840' }} />
                <span className="term-label">education.json</span>
              </div>
              <div className="edu-content">
                <div className="edu-school-name">Suffolk University</div>
                <div className="edu-location">Boston, MA</div>
                <div className="edu-line"><span className="edu-key">degree:</span> <span className="edu-val">"B.S. Computer Science"</span></div>
                <div className="edu-line"><span className="edu-key">minor:</span> <span className="edu-val">"Cybersecurity"</span></div>
                <div className="edu-line"><span className="edu-key">grad:</span> <span className="edu-val">"May 2026"</span></div>
              </div>
            </div>

            {/* Extracurriculars */}
            <div className="terminal-card">
              <div className="terminal-header">
                <span className="dot" style={{ background: '#FF5F57' }} />
                <span className="dot" style={{ background: '#FEBC2E' }} />
                <span className="dot" style={{ background: '#28C840' }} />
                <span className="term-label">extracurriculars.md</span>
              </div>
              <div className="extras-list">
                {EXTRAS.map((ex, i) => (
                  <div key={i} className="extra-item">
                    <div className="extra-role">
                      <span className="extra-arrow">▸</span> {ex.role}
                    </div>
                    <div className="extra-org">{ex.org}</div>
                    <div className="extra-desc">{ex.desc}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
